import { useCallback, useMemo } from 'react';
import { useItemSelectionStore } from '@/stores/itemSelectionStore';
import { orderedRefs, selectableAttrs, type SelectedRef } from './selection';

/**
 * Les gestes de sélection multiple, tels que l'explorateur de fichiers les a
 * appris à tout le monde : CTRL ajoute ou retire, MAJ étend depuis la dernière
 * ligne cliquée.
 *
 * CE QUE CE HOOK NE FAIT PAS : changer ce qu'un clic SIMPLE déclenche. Dans
 * l'arbre, cliquer un mail l'ouvre depuis toujours, et cliquer un dossier le
 * sélectionne comme dossier actif ; dans la page détail, un clic simple
 * sélectionne la ligne et un double-clic ouvre. Redéfinir ces gestes au nom de la
 * multi-sélection casserait l'usage principal de l'add-in pour servir un usage
 * secondaire. Les touches de modification, elles, n'étaient prises par rien.
 *
 * `onRowClick` rend VRAI quand il a traité le clic comme un geste de sélection :
 * l'appelant doit alors s'abstenir de son action habituelle (ouvrir, télécharger).
 * Sans ce retour, un CTRL+clic sélectionnerait ET téléchargerait.
 */
export function useRowSelection(scope: string) {
  const storeScope = useItemSelectionStore((s) => s.scope);
  const storeItems = useItemSelectionStore((s) => s.items);
  const anchorId = useItemSelectionStore((s) => s.anchorId);
  const setSelection = useItemSelectionStore((s) => s.setSelection);
  const clear = useItemSelectionStore((s) => s.clear);

  // Une sélection d'une AUTRE portée n'existe pas pour celle-ci (cf. le store).
  const items = useMemo(
    () => (storeScope === scope ? storeItems : []),
    [storeScope, storeItems, scope],
  );
  const ids = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  const onRowClick = useCallback(
    (e: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }, ref: SelectedRef): boolean => {
      // `metaKey` autant que `ctrlKey` : sur Mac, la touche de modification est
      // Cmd. Outlook pour Mac fait tourner le même volet.
      if (e.ctrlKey || e.metaKey) {
        const next = ids.has(ref.id) ? items.filter((i) => i.id !== ref.id) : [...items, ref];
        // L'ancre suit le dernier clic, même quand il DÉSÉLECTIONNE : c'est le
        // comportement de l'explorateur, et une plage repartant d'ailleurs
        // surprendrait.
        setSelection(scope, next, ref.id);
        return true;
      }

      if (e.shiftKey) {
        const all = orderedRefs(scope);
        const to = all.findIndex((r) => r.id === ref.id);
        const from = anchorId ? all.findIndex((r) => r.id === anchorId) : -1;
        // Pas d'ancre (ou ancre disparue : dossier replié, ligne filtrée) : la
        // plage n'a pas d'origine, on retombe sur une sélection simple plutôt que
        // de deviner.
        if (to === -1 || from === -1) {
          setSelection(scope, [ref], ref.id);
          return true;
        }
        const [lo, hi] = from <= to ? [from, to] : [to, from];
        // L'ancre NE BOUGE PAS : plusieurs MAJ+clics successifs étendent ou
        // réduisent la même plage, au lieu d'en enchaîner des morceaux.
        setSelection(scope, all.slice(lo, hi + 1), anchorId);
        return true;
      }

      // Clic simple : la multi-sélection est ABANDONNÉE (comme dans
      // l'explorateur), mais l'action propre à la ligne reste celle de la vue.
      if (items.length > 0) clear();
      return false;
    },
    [anchorId, clear, ids, items, scope, setSelection],
  );

  /**
   * Ramène la sélection à ce seul élément, et en fait l'ancre.
   *
   * À l'usage de la page détail, où un clic simple sélectionne la ligne depuis
   * toujours. L'arbre, lui, ne l'appelle pas : y cliquer un mail l'ouvre.
   */
  const selectOnly = useCallback(
    (ref: SelectedRef) => setSelection(scope, [ref], ref.id),
    [scope, setSelection],
  );

  return {
    /** Éléments sélectionnés dans cette portée, dans l'ordre où ils l'ont été. */
    items,
    selectOnly,
    /** Vrai dès que la sélection compte PLUSIEURS éléments — le seuil des actions de lot. */
    isMulti: items.length > 1,
    isSelected: useCallback((id: string) => ids.has(id), [ids]),
    onRowClick,
    clear,
    /** À étaler sur la ligne : c'est ce qui la rend repérable et ordonnable. */
    attrs: useCallback((ref: SelectedRef) => selectableAttrs(scope, ref), [scope]),
  };
}
